import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { requestMagicLink } from '@/lib/api/auth'
import { CheckCircle, Mail } from 'lucide-react'

export default function LoginSentPage() {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') || ''
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [canResend, setCanResend] = useState(true)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0 && !canResend) {
      setCanResend(true)
    }
  }, [countdown, canResend])

  const handleResend = async () => {
    if (!canResend) return

    setIsResending(true)
    setResendSuccess(false)

    try {
      await requestMagicLink(email)
      
      setResendSuccess(true)
      setCanResend(false)
      setCountdown(30) // 30 second cooldown

      // Hide success message after 3 seconds
      setTimeout(() => setResendSuccess(false), 3000)
    } catch (error) {
      console.error('Resend error:', error)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Success icon with animation */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6 animate-scale-in">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Lien envoyé !
          </h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Email confirmation */}
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg mb-6">
            <Mail className="w-5 h-5 text-foreground flex-shrink-0" />
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{email}</span>
            </p>
          </div>

          {/* Instructions */}
          <div className="space-y-4 mb-6">
            <p className="text-gray-700 text-center">
              Consultez votre boîte email et cliquez sur le lien pour vous connecter.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>⏱️</span>
              <span>Le lien expire dans 15 minutes.</span>
            </div>
          </div>

          {/* Resend section */}
          <div className="pt-6 border-t border-gray-200">
            <p className="text-sm text-muted-foreground text-center mb-3">
              Pas reçu ?
            </p>
            
            {resendSuccess && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 animate-slide-down">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700">✓ Lien renvoyé</p>
              </div>
            )}

            <Button
              onClick={handleResend}
              disabled={!canResend || isResending}
              variant="outline"
              className="w-full"
            >
              {isResending ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Envoi en cours...
                </>
              ) : !canResend ? (
                `Renvoyer dans ${countdown}s`
              ) : (
                'Renvoyer le lien'
              )}
            </Button>
          </div>
        </div>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            💡 Vérifiez aussi vos spams
          </p>
        </div>
      </div>
    </div>
  )
}
